import { useEffect, useMemo, useState } from "react";
import {
  detectSlatePresentationProfile,
  getInkDeviceInstallationId,
  INK_DEVICE_PROFILE_UPDATED_EVENT,
  INK_DEVICE_PROFILE_STORAGE_KEY,
  readLocalInkDeviceProfile,
  resolveInkRuntimeSettings,
  saveLocalInkDeviceProfile
} from "../lib/inkDeviceProfile";

function getProfileTimestamp(profile) {
  const value = profile?.updatedAt || profile?.calibratedAt;
  const timestamp = value ? new Date(value).getTime() : 0;
  return Number.isFinite(timestamp) ? timestamp : 0;
}

export default function useInkDeviceProfile({ apiBase, token } = {}) {
  const [profile, setProfile] = useState(() => readLocalInkDeviceProfile());
  const presentationProfile = useMemo(() => detectSlatePresentationProfile(), []);

  useEffect(() => {
    const handleProfileUpdate = (event) => {
      setProfile(event?.detail || readLocalInkDeviceProfile());
    };
    const handleStorageUpdate = (event) => {
      if (event.key === INK_DEVICE_PROFILE_STORAGE_KEY) {
        setProfile(readLocalInkDeviceProfile());
      }
    };
    window.addEventListener(INK_DEVICE_PROFILE_UPDATED_EVENT, handleProfileUpdate);
    window.addEventListener("storage", handleStorageUpdate);
    return () => {
      window.removeEventListener(INK_DEVICE_PROFILE_UPDATED_EVENT, handleProfileUpdate);
      window.removeEventListener("storage", handleStorageUpdate);
    };
  }, []);

  useEffect(() => {
    if (!apiBase || !token) return undefined;
    const controller = new AbortController();
    const installationId = getInkDeviceInstallationId();

    async function loadServerProfile() {
      try {
        const response = await fetch(
          `${apiBase}/api/me/ink-device-profiles?installationId=${encodeURIComponent(installationId)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal
          }
        );
        if (!response.ok) return;
        const data = await response.json();
        if (!data?.profile) return;
        const local = readLocalInkDeviceProfile();
        if (local && getProfileTimestamp(local) > getProfileTimestamp(data.profile)) return;
        const next = saveLocalInkDeviceProfile({
          ...data.profile,
          installationId,
          source: "account"
        });
        setProfile(next);
      } catch (err) {
        if (err.name !== "AbortError") {
          // A local calibration remains usable while the account profile is unavailable.
        }
      }
    }

    void loadServerProfile();
    return () => controller.abort();
  }, [apiBase, token]);

  const runtimeSettings = useMemo(
    () => resolveInkRuntimeSettings(profile, presentationProfile),
    [presentationProfile, profile]
  );

  return {
    profile,
    runtimeSettings,
    presentationProfile,
    calibrated: Boolean(profile?.calibratedAt)
  };
}
